/**
 * Shared ApexCharts styling for the Aux Sensor tab.
 *
 * The four plots in this folder previously each carried their own copy of the
 * toolbar/zoom/grid config and their own colour literals, which had already
 * drifted apart (three different greys for "excluded"). One home for both.
 */
import { ApexOptions } from 'apexcharts';

/** Brand accent, matching `cirrusBlue-400` / `--cirrus-blue-400` in the theme. */
export const AUX_ACCENT = '#03365D';

export const AUX_COLORS = {
  /** Series palette for same-type sensor instances, in order. */
  series: ['#3B82F6', '#10B981', '#F59E0B'],
  /** Slip-ratio traces. */
  ratio: '#8B5CF6',
  /** The 1:1 / ratio = 1 reference lines. */
  reference: '#9CA3AF',
  /** Least-squares fit line. */
  fit: '#EF4444',
  /** Dropped by the fit window — mid grey. */
  outsideFit: '#9CA3AF',
  /** Dropped by the min-speed threshold — light grey. */
  masked: '#D1D5DB',
};

/**
 * Chart/toolbar/grid/legend config every plot on the tab shares. Spread it and
 * override what differs, rather than restating the toolbar tool list five times.
 */
export function auxBaseOptions(height: number): ApexOptions {
  return {
    chart: {
      height,
      animations: { enabled: false },
      toolbar: {
        show: true,
        tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      },
      zoom: { enabled: true, type: 'xy' },
    },
    grid: { borderColor: '#E5E7EB', strokeDashArray: 5 },
    legend: { position: 'top', horizontalAlign: 'left' },
  };
}

/** The `ratio = 1` (sensor agreement) marker shared by both slip-ratio plots. */
export const RATIO_ONE_ANNOTATION = {
  yaxis: [{
    y: 1,
    borderColor: AUX_COLORS.reference,
    strokeDashArray: 6,
    label: { text: 'ratio = 1', style: { color: '#6B7280', background: '#F9FAFB' } },
  }],
};

/**
 * One plotted series plus its per-series styling. Apex takes styling as parallel
 * arrays rather than per-series properties, so the descriptors are declared
 * together and fanned out by `styledApexSeries` — keeping colour, marker size and
 * stroke width from drifting out of alignment with the series they belong to.
 */
export interface AuxPlotSeriesSpec {
  name: string;
  type: 'scatter' | 'line';
  data: (number | null)[][];
  color: string;
  markerSize: number;
  strokeWidth: number;
  dashArray?: number;
}

/** Scatter-point defaults — every plot on the tab draws points the same size. */
export function scatterSpec(
  name: string,
  data: (number | null)[][],
  color: string,
): AuxPlotSeriesSpec {
  return { name, type: 'scatter', data, color, markerSize: 3, strokeWidth: 0 };
}

/** Line defaults; `dashArray` > 0 gives the dashed reference-line look. */
export function lineSpec(
  name: string,
  data: (number | null)[][],
  color: string,
  dashArray = 0,
): AuxPlotSeriesSpec {
  return { name, type: 'line', data, color, markerSize: 0, strokeWidth: 2, dashArray };
}

/** Fan a descriptor list out into the parallel arrays Apex expects. */
export function styledApexSeries(specs: AuxPlotSeriesSpec[]): {
  series: { name: string; type: string; data: (number | null)[][] }[];
  options: ApexOptions;
} {
  return {
    series: specs.map((s) => ({ name: s.name, type: s.type, data: s.data })),
    options: {
      colors: specs.map((s) => s.color),
      markers: { size: specs.map((s) => s.markerSize), fillOpacity: 0.5, strokeWidth: 0 },
      stroke: {
        width: specs.map((s) => s.strokeWidth),
        dashArray: specs.map((s) => s.dashArray ?? 0),
        curve: 'straight',
      },
    },
  };
}
